// New sync APIs (do not modify existing endpoints)
import { localDb } from './localDb'
import api from '@/services/api'

export const syncService = {
  async getStatus() {
    const res = await api.get('/sync/status')
    return res.data
  },

  async getLocalSyncStatus() {
    try {
      const [dirtyCustomers, dirtyOrders, dirtyOrderItems] = await Promise.all([
        localDb.customers.filter(r => r._dirty === 1).count(),
        localDb.orders.filter(r => r._dirty === 1).count(),
        localDb.orderItems.filter(r => r._dirty === 1).count()
      ])
      
      const lastSyncAt = await localDb.getLastSyncAt()
      
      return {
        pendingUpload: {
          customers: dirtyCustomers,
          orders: dirtyOrders,
          orderItems: dirtyOrderItems,
          total: dirtyCustomers + dirtyOrders + dirtyOrderItems
        },
        lastSyncAt,
        needsSync: (dirtyCustomers + dirtyOrders + dirtyOrderItems) > 0
      }
    } catch (error) {
      console.error('Error getting local sync status:', error)
      return {
        pendingUpload: { customers: 0, orders: 0, orderItems: 0, total: 0 },
        lastSyncAt: null,
        needsSync: false
      }
    }
  },

  async batchUpload(payload) {
    const res = await api.post('/sync/batch-upload', payload)
    return res.data
  },

  async batchDownload(lastSyncAt) {
    const res = await api.post('/sync/batch-download', { lastSyncAt })
    return res.data
  },

  // Merge helper with simple conflict rule: last-write-wins on updatedAt
  async upsertWithConflict(table, incoming) {
    const current = await table.get(incoming.id || incoming.serverId)
    if (!current) {
      await table.put({ ...incoming, _dirty: 0 })
      return 'insert'
    }
    const localDirty = current._dirty === 1
    const localNewer = (new Date(current.updatedAt || 0)) > (new Date(incoming.updatedAt || 0))
    if (localDirty && localNewer) {
      // keep local change; will be uploaded next sync
      return 'kept-local'
    }
    await table.update(current.id || incoming.id, { ...incoming, _dirty: 0 })
    return 'update'
  },

  // High-level one-click sync
  async syncAll() {
    const startedAt = new Date().toISOString()
    const sessionId = await localDb.syncJournal.add({ type: 'manual', startedAt, success: false })

    try {
      console.log('🔄 Starting sync process...')
      
      // Build upload payload of dirty records
      const [customers, orders, orderItems] = await Promise.all([
        localDb.customers.filter(r => r._dirty === 1).toArray(),
        localDb.orders.filter(r => r._dirty === 1).toArray(),
        localDb.orderItems.filter(r => r._dirty === 1).toArray()
      ])

      console.log(`📤 Uploading: ${customers.length} customers, ${orders.length} orders, ${orderItems.length} order items`)

      // Prepare orders with proper structure for server
      const ordersForUpload = orders.map(order => ({
        id: order.serverId || order.id,
        serverId: order.serverId || order.id,
        status: order.status,
        isPickup: order.isPickup || false,
        isInternalOrder: order.isInternalOrder || false,
        tableId: order.tableId,
        customerId: order.customerId,
        totalAmount: order.totalAmount || 0,
        subtotalAmount: order.subtotalAmount || 0,
        taxAmount: order.taxAmount || 0,
        taxPercentage: order.taxPercentage || 0,
        deliveryFee: order.deliveryFee || 0,
        notes: order.notes,
        paymentMethod: order.paymentMethod,
        cashAmount: order.cashAmount,
        posAmount: order.posAmount,
        cardTransferAmount: order.cardTransferAmount,
        deliveryAddress: order.deliveryAddress,
        employeeId: order.employeeId,
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        closedAt: order.closedAt,
        _dirty: order._dirty
      }))

      // Prepare order items with proper structure
      const orderItemsForUpload = orderItems.map(item => ({
        id: item.serverId || item.id,
        serverId: item.serverId || item.id,
        orderId: item.orderId,
        menuItemId: item.menuItemId,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        totalPrice: item.totalPrice || 0,
        menuItemName: item.menuItemName,
        menuItemImage: item.menuItemImage,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        _dirty: item._dirty
      }))

      const uploadResult = await this.batchUpload({ 
        customers: customers, 
        orders: ordersForUpload, 
        orderItems: orderItemsForUpload 
      })
      
      console.log('📤 Upload result:', uploadResult)
      await localDb.logs.add({ 
        level: 'info', 
        scope: 'upload', 
        createdAt: new Date().toISOString(), 
        details: JSON.stringify({ 
          counts: { customers: customers.length, orders: orders.length, orderItems: orderItems.length },
          result: uploadResult
        }) 
      })

      // Apply server mappings (clientTempId -> serverId) and clear dirty flags
      if (uploadResult?.mappings?.length) {
        console.log('🔄 Applying server mappings...')
        // Persist mappings
        for (const m of uploadResult.mappings) {
          await localDb.idMap.put({
            clientTempId: m.clientTempId || m.localId,
            serverId: m.serverId,
            entity: m.entity
          })
        }

        // Update local entities with serverId and clear dirty
        for (const m of uploadResult.mappings) {
          if (m.entity === 'customer') {
            await localDb.customers.update(m.localId || m.clientTempId, { serverId: m.serverId, _dirty: 0 })
          }
          if (m.entity === 'order') {
            await localDb.orders.update(m.localId || m.clientTempId, { serverId: m.serverId, _dirty: 0 })
            // Repoint orderItems' orderId from temp to real where applicable
            const items = await localDb.orderItems.where('orderId').equals(m.localId || m.clientTempId).toArray()
            for (const it of items) {
              await localDb.orderItems.update(it.id, { orderId: m.serverId, _dirty: 0 })
            }
          }
          if (m.entity === 'orderItem') {
            await localDb.orderItems.update(m.localId || m.clientTempId, { serverId: m.serverId, _dirty: 0 })
          }
        }
      } else if (uploadResult?.clearedIds) {
        console.log('🔄 Clearing dirty flags...')
        // Fallback clear flags by local ids if provided
        const { customers: cIds = [], orders: oIds = [], orderItems: oiIds = [] } = uploadResult.clearedIds
        await Promise.all([
          ...cIds.map(id => localDb.customers.update(id, { _dirty: 0 })),
          ...oIds.map(id => localDb.orders.update(id, { _dirty: 0 })),
          ...oiIds.map(id => localDb.orderItems.update(id, { _dirty: 0 }))
        ])
      }

      // Download deltas
      const lastSyncAt = await localDb.getLastSyncAt()
      console.log('📥 Downloading changes since:', lastSyncAt)
      const download = await this.batchDownload(lastSyncAt)
      await localDb.logs.add({ 
        level: 'info', 
        scope: 'download', 
        createdAt: new Date().toISOString(), 
        details: JSON.stringify({ lastSyncAt, downloadCounts: {
          customers: download.customers?.length || 0,
          orders: download.orders?.length || 0,
          orderItems: download.orderItems?.length || 0
        }}) 
      })

      // Merge with basic last-write-wins
      let stats = { inserted: 0, updated: 0, keptLocal: 0 }
      for (const c of (download.customers || [])) {
        const r = await this.upsertWithConflict(localDb.customers, c)
        if (r === 'insert') stats.inserted++; else if (r === 'update') stats.updated++; else stats.keptLocal++
      }
      for (const o of (download.orders || [])) {
        const r = await this.upsertWithConflict(localDb.orders, o)
        if (r === 'insert') stats.inserted++; else if (r === 'update') stats.updated++; else stats.keptLocal++
      }
      for (const oi of (download.orderItems || [])) {
        const r = await this.upsertWithConflict(localDb.orderItems, oi)
        if (r === 'insert') stats.inserted++; else if (r === 'update') stats.updated++; else stats.keptLocal++
      }

      await localDb.setLastSyncAt(new Date().toISOString())
      await localDb.syncJournal.update(sessionId, { finishedAt: new Date().toISOString(), success: true, stats })
      
      console.log('✅ Sync completed successfully:', stats)
      return { success: true, stats }
    } catch (err) {
      console.error('❌ Sync failed:', err)
      await localDb.syncJournal.update(sessionId, { finishedAt: new Date().toISOString(), success: false, error: String(err) })
      try { await localDb.logs.add({ level: 'error', scope: 'sync', createdAt: new Date().toISOString(), details: String(err) }) } catch {}
      throw err
    }
  }
}


